import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { trackTrialLead } from "@/lib/analytics";

export default function BookTrial() {
    const [form, setForm] = useState({
        name: "",
        mobile: "",
        email: "",
        city: "",
        product_slug: "",
        preferred_date: "",
        preferred_time: "",
        message: "",
    });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [products, setProducts] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        document.title = "Book a Free Styletto IX Trial | signiastylettoix.com";
        api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    }, []);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.mobile) {
            toast.error("Name and mobile are required");
            return;
        }
        setLoading(true);
        try {
            const product = products.find((p) => p.slug === form.product_slug);
            await api.post("/leads", {
                type: "trial",
                ...form,
                product_name: product ? product.short_name : null,
                source_page: "/book-trial",
                whatsapp_consent: true,
            });
            try { trackTrialLead({ city: form.city }); } catch (e) { /* never break */ }
            setDone(true);
            window.scrollTo(0, 0);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    if (done) {
        return (
            <section className="section">
                <div className="container-x max-w-[640px] text-center">
                    <CheckCircle2 size={56} className="text-[#003E4D] mx-auto" />
                    <h1 className="font-serif text-[40px] md:text-[52px] mt-6">Trial Confirmed.</h1>
                    <p className="mt-4 text-[16px] text-[#4a5568] leading-relaxed">
                        A HearClear audiologist will reach out within 24 hours to schedule your free
                        Styletto IX trial — at home or your nearest HearClear clinic.
                    </p>
                    <button className="btn-primary mt-7 mx-auto" onClick={() => navigate("/")} data-testid="trial-back-home">
                        Back to home <ArrowRight size={16} />
                    </button>
                </div>
            </section>
        );
    }

    return (
        <section className="section">
            <div className="container-x grid lg:grid-cols-12 gap-12">
                <div className="lg:col-span-5">
                    <p className="eyebrow">Free Trial</p>
                    <h1 className="font-serif text-[44px] md:text-[60px] leading-[1.04] mt-3">
                        Try Styletto IX <span className="italic text-[#003E4D]">risk-free for 3 days.</span>
                    </h1>
                    <p className="mt-5 text-[16px] text-[#4a5568] leading-relaxed">
                        We'll fit your Styletto IX at your home or any HearClear clinic — programmed to
                        your audiogram by an RCI-certified audiologist. Don't love it? Return it. Full refund.
                    </p>
                </div>
                <form
                    onSubmit={submit}
                    className="lg:col-span-7 bg-white rounded-[28px] p-6 md:p-10 border border-black/5 shadow-[0_30px_70px_-25px_rgba(0,0,0,0.12)]"
                    data-testid="trial-form"
                >
                    <h2 className="font-serif text-[28px] md:text-[34px] leading-tight">Book your slot</h2>
                    <p className="text-[14px] text-[#4a5568] mt-2">A HearClear audiologist will contact you within 24 hours.</p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                        <Inp data-testid="trial-name" required placeholder="Full name *" value={form.name} onChange={set("name")} />
                        <Inp data-testid="trial-mobile" required placeholder="Mobile *" value={form.mobile} onChange={set("mobile")} />
                        <Inp data-testid="trial-email" type="email" placeholder="Email" value={form.email} onChange={set("email")} />
                        <Inp data-testid="trial-city" placeholder="City" value={form.city} onChange={set("city")} />
                        <Sel data-testid="trial-product" value={form.product_slug} onChange={set("product_slug")}>
                            <option value="">Interested in (any model) — optional</option>
                            {products.map((p) => (
                                <option key={p.slug} value={p.slug}>{p.short_name || p.name}</option>
                            ))}
                        </Sel>
                        <Inp data-testid="trial-date" type="date" placeholder="Preferred date" value={form.preferred_date} onChange={set("preferred_date")} />
                        <Sel data-testid="trial-time" value={form.preferred_time} onChange={set("preferred_time")} className="md:col-span-2">
                            <option value="">Preferred time</option>
                            <option>10 AM – 12 PM</option>
                            <option>12 PM – 3 PM</option>
                            <option>3 PM – 6 PM</option>
                            <option>6 PM – 8 PM</option>
                        </Sel>
                        <textarea
                            data-testid="trial-message"
                            placeholder="Anything we should know? (optional)"
                            value={form.message}
                            onChange={set("message")}
                            rows={3}
                            className="md:col-span-2 px-4 py-3 rounded-xl border border-[#E5E7EB] bg-white text-[15px] focus:outline-none focus:border-[#003E4D] focus:ring-2 focus:ring-[#003E4D]/15"
                        />
                    </div>
                    <button type="submit" disabled={loading} className="btn-primary mt-7 w-full justify-center md:w-auto !px-10" data-testid="trial-submit">
                        {loading ? "Booking…" : "Book Free Trial"} <ArrowRight size={16} />
                    </button>
                </form>
            </div>
        </section>
    );
}

const Inp = (props) => (
    <input className="h-12 px-4 rounded-xl border border-[#E5E7EB] bg-white text-[15px] focus:outline-none focus:border-[#003E4D] focus:ring-2 focus:ring-[#003E4D]/15" {...props} />
);
const Sel = ({ children, className = "", ...rest }) => (
    <select className={`h-12 px-3 rounded-xl border border-[#E5E7EB] bg-white text-[15px] focus:outline-none focus:border-[#003E4D] focus:ring-2 focus:ring-[#003E4D]/15 ${className}`} {...rest}>{children}</select>
);
